import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";

import { Icon, icons } from "../../../components/Icon";
import {
  AuthCard,
  AuthContainer,
  BackButton,
  ButtonSection,
  ErrorMessage,
  FormSection,
  HeaderSection,
  InputContainer,
  InputGroup,
  PrimaryButton,
  ServerErrorAlert,
  StyledInput,
  Title,
} from "../shared/authStyles";
import { AnimatePresence, motion } from "../shared/motionUtils";
import ForgotPasswordSuccess from "./ForgotPasswordSuccess";

// Validation schema for forgot password
const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

const SuccessMessage = styled(motion.div)`
  color: #1e2223;
  font-family: ${({ theme }) => theme.defaultFontFamily};
  font-size: 14px;
  margin-top: 4px;
  padding: 8px 12px;
  background: #efe;
  border: 1px solid #cfc;
  border-radius: 4px;
`;

const ForgotPassword = ({
  forgotPassword,
  forgot,
  clearForgotPasswordLinkSent,
}) => {
  const history = useHistory();
  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submittedEmail, setSubmittedEmail] = useState("");
  const emailRef = useRef(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // Check if user is already logged in
  useEffect(() => {
    let token = localStorage.getItem("access_token");
    if (token != null) {
      history.push("/user/dashboard");
    }
    return () => {
      clearForgotPasswordLinkSent();
    };
  }, [history, clearForgotPasswordLinkSent]);

  // Auto-focus email field on component mount
  useEffect(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  // Handle Redux state changes
  useEffect(() => {
    if (forgot?.linkSent) {
      // Success state is handled in the render
    }
  }, [forgot?.linkSent]);

  // Handle error messages from Redux
  useEffect(() => {
    if (forgot?.errorMessage) {
      if (forgot.errorMessage === "No user with this email") {
        setFieldErrors({ email: "There is no account with this email!" });
      } else {
        setServerError(forgot.errorMessage);
      }
    }
  }, [forgot?.errorMessage]);

  const onSubmit = useCallback(
    data => {
      setServerError(null);
      setFieldErrors({});
      setSubmittedEmail(data.email);

      const payload = {
        email: data.email,
      };
      forgotPassword(payload);
    },
    [forgotPassword],
  );

  const handleBack = useCallback(() => {
    history.push("/auth/login");
  }, [history]);

  const handleFieldChange = useCallback(
    (fieldName, onChange) => e => {
      // Clear server errors when user starts typing
      if (fieldErrors[fieldName]) {
        setFieldErrors(prev => ({ ...prev, [fieldName]: null }));
      }
      if (serverError) {
        setServerError(null);
      }
      onChange(e);
    },
    [fieldErrors, serverError],
  );

  // Show success state if link was sent
  if (forgot?.linkSent) {
    return <ForgotPasswordSuccess email={submittedEmail} />;
  }

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <HeaderSection
          marginBottom="0"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Title
            margin="0"
            fontSize="32px"
            lineHeight="48px"
            color="#1e2223"
            fontWeight="700"
            textAlign="left"
          >
            Forgot Password?
          </Title>
        </HeaderSection>

        <FormSection
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          marginTop="32px"
          noValidate
        >
          <InputContainer>
            <InputGroup>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <StyledInput
                    {...field}
                    ref={emailRef}
                    onChange={handleFieldChange("email", field.onChange)}
                    type="email"
                    placeholder="Enter your email"
                    autoComplete="email"
                    hasError={!!errors.email || !!fieldErrors.email}
                  />
                )}
              />
              <AnimatePresence>
                {fieldErrors.email ? (
                  <ErrorMessage
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {fieldErrors.email}
                  </ErrorMessage>
                ) : (
                  errors.email && (
                    <ErrorMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {errors.email.message}
                    </ErrorMessage>
                  )
                )}
              </AnimatePresence>
            </InputGroup>
          </InputContainer>

          <AnimatePresence>
            {serverError && (
              <ServerErrorAlert
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {serverError}
              </ServerErrorAlert>
            )}
          </AnimatePresence>

          <ButtonSection direction="row" gap="12px" width="100%">
            <BackButton
              type="button"
              onClick={handleBack}
              disabled={forgot?.isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon component={icons.back} size="20px" /> Back
            </BackButton>

            <PrimaryButton
              type="submit"
              disabled={forgot?.isLoading || isSubmitting}
              flex="1"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {forgot?.isLoading ? "Sending..." : "Send Reset Link"}
            </PrimaryButton>
          </ButtonSection>
        </FormSection>
      </AuthCard>
    </AuthContainer>
  );
};

export default ForgotPassword;
